import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Button, Form } from 'react-bootstrap';
import InputGroup from 'react-bootstrap/InputGroup';
import DropdownButton from 'react-bootstrap/DropdownButton';
import Dropdown from 'react-bootstrap/Dropdown';
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import { Drawer } from 'rsuite';
import { toast } from 'react-toastify';
import { faSearch, faTimes, faEdit, faCheck, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Loading, PageHeader, Pagination } from '../../components';
import DataTable from '../../components/DataTable/DataTable';
import CancelAlertPopup from '../../components/CancelAlertPopup/CancelAlertPopup';
import useComponentState from '../../utils/useComponentState';
import { getAllUsersPaged, updateUserMobileNumber } from '../../api/user';
import * as FileSaver from 'file-saver';
import { deactivateAccounts, reactivateAccounts, searchAccounts, downloadAccounts } from '../../api/account';
import { validateMobileNumber } from '../../utils/validations';
import PermissionGate from '../../components/PermissionGate/PermissionGate';
import { usePermissions } from '../../hooks/usePermissions';
import { USER_MANAGEMENT_PERMISSIONS } from '../../constants/permissions';
import styles from './AccountStatusManagement.module.scss';

const DOWNLOAD_PAGE_SIZE = 100000;

const AccountStatusManagement = ({ auth }) => {
  const { can } = usePermissions();
  const canManage = can(USER_MANAGEMENT_PERMISSIONS.VIEW_CUSTOMERS.MANAGE);
  const canUpdateMobile = can(USER_MANAGEMENT_PERMISSIONS.VIEW_CUSTOMERS.UPDATE_MOBILE);
  const canFilter = can(USER_MANAGEMENT_PERMISSIONS.VIEW_CUSTOMERS.FILTER);
  const [state, setState] = useComponentState({
    users: [],
    loading: false,
    currentPage: 0,
    pageSize: 25,
    totalPages: 0,
    loadError: '',
    showDrawer: false,
    selectedUser: null,
    drawerMode: 'view',
    dpdStatus: '0',
    actionLoading: false,
    showReactivateConfirm: false,
    editingMobile: false,
    newMobileNo: '',
    editMobileError: '',
    editMobileSaving: false,
    statusFilter: '',
    searchKey: '',
    searchType: 'mobileNo',
    isClearable: false,
    label: 'Mobile Number',
    searchPlaceholder: 'Mobile Number',
    downloadStatus: '',
  });

  useEffect(() => {
    fetchUsers(0, 25);
    // eslint-disable-next-line
  }, []);

  
  const buildSearchPayload = (page, size, statusFilter, searchKey, searchType) => ({
    mobileNo: searchType === 'mobileNo' ? searchKey || null : null,
    cusRef: searchType === 'cusRef' ? searchKey || null : null,
    status: statusFilter ? statusFilter.toLowerCase() : null,
    page,
    pageSize: size,
    sortBy: 'created_date',
    order: 'desc',
  });

  const fetchUsers = async (page = 0, size = 25, statusFilter = state.statusFilter, searchKey = state.searchKey, searchType = state.searchType) => {
    setState({ loading: true, loadError: '' });
    try {
      let res;
      if (statusFilter || searchKey) {
        res = await searchAccounts(
          auth.auth.token,
          buildSearchPayload(page, size, statusFilter, searchKey, searchType)
        );
        setState({
          users: (res.users || res.list || []).map((item) => ({
            ...item,
            cusRef: item.cusRef || item.voucherId,
            status: item.status?.toUpperCase(),
          })),
          totalPages: res.total || res.totalPageCount || 0,
          loading: false,
        });
      } else {
        res = await getAllUsersPaged(auth.auth.token, page, size);
        setState({
          users: res.users || [],
          totalPages: res.total || 0,
          loading: false,
        });
      }
    } catch (err) {
      setState({ loadError: 'Failed to load accounts. Please try again.', loading: false });
    }
  };

  const isAnyFilterActive = !!state.statusFilter || (!!state.searchKey && state.isClearable);

  const handleDownload = async () => {
    setState({ downloadStatus: 'DOWNLOADING' });
    try {
      const payload = buildSearchPayload(
        0,
        DOWNLOAD_PAGE_SIZE,
        state.statusFilter,
        state.isClearable ? state.searchKey : '',
        state.searchType
      );
      const blob = await downloadAccounts(auth.auth.token, payload);
      FileSaver.saveAs(
        blob,
        `${isAnyFilterActive ? 'Customer_Details_Filtered' : 'Customer_Details'}.csv`
      );
      setState({ downloadStatus: '' });
    } catch (err) {
      setState({ downloadStatus: '' });
      toast.error(err?.message || 'Failed to download. Please try again.');
    }
  };

  const handleSearch = () => {
    if (!state.isClearable) {
      if (state.searchKey) {
        setState({ isClearable: true, currentPage: 0 });
        fetchUsers(0, state.pageSize, state.statusFilter, state.searchKey, state.searchType);
      }
    } else {
      setState({ searchKey: '', isClearable: false, currentPage: 0 });
      fetchUsers(0, state.pageSize, state.statusFilter, '', state.searchType);
    }
  };

  const searchOnChange = (e) => {
    const val = e.target.value;
    setState({ searchKey: val, isClearable: val.length > 0 ? state.isClearable : false });
  };

  const handleFilterChange = (filter) => {
    setState({ statusFilter: filter, currentPage: 0 });
    fetchUsers(0, state.pageSize, filter, state.searchKey, state.searchType);
  };

  const handlePagination = (selected) => {
    setState({ currentPage: selected });
    fetchUsers(selected, state.pageSize, state.statusFilter, state.searchKey, state.searchType);
  };

  const handleRowClick = (row) => {
    if (!row) return;
    setState({ showDrawer: true, selectedUser: row, drawerMode: 'view', dpdStatus: '0', editingMobile: false, newMobileNo: '', editMobileError: '' });
  };

  const handleCloseDrawer = () => {
    setState({ showDrawer: false, selectedUser: null, drawerMode: 'view', dpdStatus: '0', editingMobile: false, newMobileNo: '', editMobileError: '' });
  };

  const stripDelPrefix = (str) => str ? str.replace(/^DEL[\s-]*/i, '') : str;

  const getAccountId = (user) => user?.cusRef;

  const handleDeactivateConfirm = async () => {
    if (!canManage) return;
    const user = state.selectedUser;
    const accountId = getAccountId(user);
    if (!accountId) {
      toast.error('Could not determine account ID.');
      return;
    }
    setState({ actionLoading: true });
    try {
      const result = await deactivateAccounts(auth.auth.token, [
        { accountId: String(accountId), dpdStatus: state.dpdStatus },
      ]);
      if (result.failedCount > 0) {
        console.error('Deactivation failed records:', result.failedRecords);
        toast.error('Account deactivation failed. Please try again or contact support.');
      } else {
        toast.success('Account deactivated successfully.');
        handleCloseDrawer();
        fetchUsers(state.currentPage, state.pageSize, state.statusFilter);
      }
    } catch (err) {
      console.error('Deactivation error:', err);
      toast.error('Something went wrong while deactivating the account. Please try again.');
    }
    setState({ actionLoading: false });
  };

  const handleReactivateConfirm = async () => {
    if (!canManage) return;
    const user = state.selectedUser;
    const accountId = getAccountId(user);
    if (!accountId) {
      toast.error('Could not determine account ID.');
      return;
    }
    setState({ actionLoading: true, showReactivateConfirm: false });
    try {
      await reactivateAccounts(auth.auth.token, [String(accountId)]);
      toast.success('Account reactivated successfully.');
      handleCloseDrawer();
      fetchUsers(state.currentPage, state.pageSize, state.statusFilter);
    } catch (err) {
      console.error('Reactivation error:', err);
      const message = err?.message || '';
      if (message.startsWith('ACCOUNT_ALREADY_ACTIVE_FOR_MOBILE_NO:')) {
        const mobileNo = message.split(':').slice(1).join(':').trim();
        toast.info(`This Account Already Active For Mobile Number ${mobileNo}`);
      } else if (message.startsWith('ACCOUNT_ALREADY_ACTIVE:')) {
        const cusRef = message.split(':')[1].trim();
        toast.info(`This Account Already Active with ${cusRef} Cus Ref`);
      } else {
        toast.error('Something went wrong while reactivating the account. Please try again.');
      }
    }
    setState({ actionLoading: false });
  };

  const handleEditMobileStart = () => {
    const user = state.selectedUser;
    setState({ editingMobile: true, newMobileNo: stripDelPrefix(user?.mobileNo) || '', editMobileError: '' });
  };

  const handleEditMobileCancel = () => {
    setState({ editingMobile: false, newMobileNo: '', editMobileError: '' });
  };

  const handleEditMobileChange = (value) => {
    setState({ newMobileNo: value, editMobileError: '' });
  };

  const handleEditMobileSave = async () => {
    if (!canUpdateMobile) return;
    const user = state.selectedUser;
    if (!user) return;

    const validation = validateMobileNumber(state.newMobileNo);
    if (!validation.isValid) {
      setState({ editMobileError: validation.error });
      return;
    }

    setState({ editMobileSaving: true });
    try {
      await updateUserMobileNumber(auth.auth.token, {
        userId: user.id || user.userId,
        currentMobileNo: stripDelPrefix(user.mobileNo),
        newMobileNo: state.newMobileNo.trim(),
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
      });
      toast.success('Mobile number updated successfully.');
      setState({
        selectedUser: { ...user, mobileNo: state.newMobileNo.trim() },
        editingMobile: false,
        newMobileNo: '',
        editMobileError: '',
        editMobileSaving: false,
        users: state.users.map((u) => (u === user ? { ...u, mobileNo: state.newMobileNo.trim() } : u)),
      });
    } catch (err) {
      console.error('Edit mobile number error:', err);
      toast.error('Failed to update mobile number. Please try again.');
      setState({ editMobileSaving: false });
    }
  };

  const renderSearch = () => (
    <InputGroup className="mb-3">
      <DropdownButton
        as={InputGroup.Prepend}
        variant="secondary"
        title={state.label}
        id="customer-search-dropdown"
      >
        <Dropdown.Item
          onClick={() => setState({ label: 'Mobile Number', searchPlaceholder: 'Mobile Number', searchType: 'mobileNo', searchKey: '', isClearable: false })}
        >
          Mobile Number
        </Dropdown.Item>
      </DropdownButton>
      <Form.Control
        placeholder={state.searchPlaceholder}
        value={state.searchKey}
        onChange={searchOnChange}
        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
        maxLength={50}
      />
      <InputGroup.Append>
        <Button variant="secondary" onClick={handleSearch}>
          <FontAwesomeIcon icon={state.isClearable ? faTimes : faSearch} />
        </Button>
      </InputGroup.Append>
    </InputGroup>
  );

  const renderFilterByStatus = () => (
    <Breadcrumb>
      {[
        { key: '', label: 'All' },
        { key: 'ACTIVE', label: 'Active' },
        { key: 'INACTIVE', label: 'Inactive' },
      ].map(({ key, label }) => (
        <Breadcrumb.Item
          key={key}
          active={state.statusFilter === key}
          onClick={() => handleFilterChange(key)}
        >
          {label}
        </Breadcrumb.Item>
      ))}
    </Breadcrumb>
  );

  const renderDrawer = () => {
    const user = state.selectedUser;
    if (!user) return null;

    const accountId = stripDelPrefix(getAccountId(user)) || 'N/A';
    const mobileNo = stripDelPrefix(user.mobileNo) || 'N/A';
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.name || user.ownerName || 'N/A';
    const status = user.status || 'N/A';
    const isActive = status === 'ACTIVE';
    const isInactive = status === 'INACTIVE';
    const pointsEarned = user.pointsEarned ?? 'N/A';
    const pointsRedeemed = user.pointsRedeemed ?? 'N/A';
    const pointsOutstanding = user.pointsOutstanding ?? 'N/A';

    if (state.drawerMode === 'deactivate') {
      return (
        <>
          <Drawer.Header>
            <div className={styles.drawer_header}>
              <Drawer.Title>Deactivate Account</Drawer.Title>
            </div>
          </Drawer.Header>
          <Drawer.Body>
            <div className={styles.drawer_body}>
              <div className={styles.detail_row}>
                <strong>Account ID:</strong>
                <span>{accountId}</span>
              </div>
              <div className={styles.detail_row}>
                <strong>Mobile Number:</strong>
                <span>{mobileNo}</span>
              </div>
              <div className={styles.detail_row}>
                <strong>Name:</strong>
                <span>{name}</span>
              </div>

              <p className={styles.section_label}>Select deactivation type:</p>

              {[
                { val: '0', label: 'Full Deactivation', description: 'Account will be deactivated and all access will be revoked.' },
                { val: '30', label: 'Partial Deactivation', description: 'Points will be reset, but the point account will remain.' },
                { val: '60', label: 'Partial Deactivation', description: 'Points will be reset, but the point account will remain.' },
              ].map(({ val, label, description }) => (
                <label key={val} className={styles.radio_label}>
                  <input
                    type="radio"
                    name="dpdStatus"
                    value={val}
                    checked={state.dpdStatus === val}
                    onChange={() => setState({ dpdStatus: val })}
                  />
                  <div className={styles.radio_content}>
                    <span className={styles.radio_title}>{val} ({label})</span>
                    <span className={styles.radio_description}>{description}</span>
                  </div>
                </label>
              ))}
            </div>

            <div className={styles.drawer_actions}>
              <button
                className="btn btn-secondary"
                onClick={() => setState({ drawerMode: 'view' })}
                disabled={state.actionLoading}
              >
                Back
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeactivateConfirm}
                disabled={state.actionLoading}
              >
                {state.actionLoading ? 'Processing...' : 'Confirm Deactivation'}
              </button>
            </div>
          </Drawer.Body>
        </>
      );
    }

    return (
      <>
        <Drawer.Header>
          <div className={styles.drawer_header}>
            <Drawer.Title>Account Details</Drawer.Title>
          </div>
        </Drawer.Header>
        <Drawer.Body>
          <div className={styles.drawer_body}>
            <div className={styles.detail_row}>
              <strong>Account ID:</strong>
              <span>{accountId}</span>
            </div>
            <div className={styles.detail_row}>
              <strong>Mobile Number:</strong>
              {state.editingMobile ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="text"
                    value={state.newMobileNo}
                    onChange={(e) => handleEditMobileChange(e.target.value)}
                    maxLength={12}
                    style={{ borderColor: state.editMobileError ? '#dc3545' : '' }}
                    autoFocus
                  />
                  <span style={{ display: 'flex', gap: 8 }}>
                    <i
                      className="bi bi-check2 text-primary"
                      style={{ cursor: 'pointer', fontSize: '1.2em' }}
                      title="Save"
                      onClick={handleEditMobileSave}
                    >
                      <FontAwesomeIcon icon={state.editMobileSaving ? faSpinner : faCheck} spin={state.editMobileSaving} />
                    </i>
                    <i
                      className="bi bi-x text-primary"
                      style={{ cursor: 'pointer', fontSize: '1.2em' }}
                      title="Cancel"
                      onClick={handleEditMobileCancel}
                    >
                      <FontAwesomeIcon icon={faTimes} style={{ color: 'red' }} />
                    </i>
                  </span>
                </div>
              ) : (
                <span className={styles.editable_field}>
                  <span>{mobileNo}</span>
                  {isActive && (
                    <PermissionGate permission={USER_MANAGEMENT_PERMISSIONS.VIEW_CUSTOMERS.UPDATE_MOBILE}>
                      <button
                        type="button"
                        className="btn btn-link p-0 m-0 align-baseline"
                        style={{ fontSize: '0.95em', color: '#0d6efd', textDecoration: 'none', marginLeft: '8px' }}
                        onClick={handleEditMobileStart}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                    </PermissionGate>
                  )}
                </span>
              )}
            </div>
            {state.editingMobile && state.editMobileError && (
              <div className={styles.error_message} style={{ textAlign: 'right' }}>{state.editMobileError}</div>
            )}
            <div className={styles.detail_row}>
              <strong>Name:</strong>
              <span>{name}</span>
            </div>
            <div className={styles.detail_row}>
              <strong>Status:</strong>
              <span className={isActive ? styles.status_active : isInactive ? styles.status_inactive : ''}>
                {status}
              </span>
            </div>
            <div className={styles.detail_row}>
              <strong>Points Earned:</strong>
              <span>{pointsEarned}</span>
            </div>
            <div className={styles.detail_row}>
              <strong>Points Redeemed:</strong>
              <span>{pointsRedeemed}</span>
            </div>
            <div className={styles.detail_row}>
              <strong>Points Outstanding:</strong>
              <span>{pointsOutstanding}</span>
            </div>
          </div>

          <div className={styles.drawer_actions}>
            {isActive && (
              <PermissionGate permission={USER_MANAGEMENT_PERMISSIONS.VIEW_CUSTOMERS.MANAGE}>
                <button
                  className="btn btn-danger"
                  onClick={() => setState({ drawerMode: 'deactivate' })}
                  disabled={state.actionLoading}
                >
                  Deactivate
                </button>
              </PermissionGate>
            )}
            {isInactive && (
              <PermissionGate permission={USER_MANAGEMENT_PERMISSIONS.VIEW_CUSTOMERS.MANAGE}>
                <button
                  className="btn btn-success"
                  style={{ display: 'none' }}
                  onClick={() => setState({ showReactivateConfirm: true })}
                  disabled={state.actionLoading}
                >
                  Reactivate
                </button>
              </PermissionGate>
            )}
          </div>
        </Drawer.Body>
      </>
    );
  };

  if (!can(USER_MANAGEMENT_PERMISSIONS.VIEW_CUSTOMERS.VIEW)) {
    return (
      <div className={styles.root_div}>
        <PageHeader text="Customer Details" />
        <div className={styles.access_denied}>
          <p className={styles.access_denied_title}>Access Denied</p>
          <p className={styles.access_denied_message}>
            You do not have permission to access this feature. Please contact your administrator.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root_div}>
      {state.loading ? (
        <Loading text="Loading" />
      ) : (
        <div>
          <PageHeader text="Customer Details" />
          {state.loadError ? (
            <p className={styles.error_text}>{state.loadError}</p>
          ) : (
            <div>
              <div className={styles.search_area_block}>
                {canFilter && renderFilterByStatus()}
                <div className={styles.filter_row}>
                  {canFilter && <div className={styles.search_area}>{renderSearch()}</div>}
                  <PermissionGate permission={USER_MANAGEMENT_PERMISSIONS.VIEW_CUSTOMERS.DOWNLOAD}>
                    <div className={styles.download_button_block}>
                      <Button
                        variant="secondary"
                        onClick={handleDownload}
                        disabled={state.downloadStatus === 'DOWNLOADING'}
                      >
                        {state.downloadStatus === 'DOWNLOADING'
                          ? 'Downloading...'
                          : isAnyFilterActive
                          ? 'Download'
                          : 'Download All'}
                      </Button>
                    </div>
                  </PermissionGate>
                </div>
              </div>
              <DataTable
                tableData={state.users}
                tableheading={{ column: ['Account ID', 'Mobile Number', 'Name', 'Status'] }}
                handleOnClick={handleRowClick}
                tag="customer details"
                fetchError=""
                searchFilterLoading={false}
              />
              {!state.loading && (
                <Pagination
                  pageCount={state.totalPages}
                  onChange={handlePagination}
                  currentPage={state.currentPage}
                />
              )}
            </div>
          )}
        </div>
      )}

      <CancelAlertPopup
        show={state.showReactivateConfirm}
        alertMessage={`Reactivate account for ${stripDelPrefix(state.selectedUser?.mobileNo) || stripDelPrefix(state.selectedUser?.cusRef)}? This will restore the account to ACTIVE status.`}
        handleYesBtnClick={handleReactivateConfirm}
        handleNoBtnClick={() => setState({ showReactivateConfirm: false })}
      />

      <Drawer show={state.showDrawer} onHide={handleCloseDrawer} placement="right">
        {renderDrawer()}
      </Drawer>
    </div>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});
export default connect(mapStateToProps)(AccountStatusManagement);
